import { useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import { Eye, Loader2, Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { APIError, type PostWeather } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useUpdatePost } from "@/features/feed/hooks/use-update-post.ts";
import type { Post } from "@/features/feed/types.ts";
import { WEATHER_OPTIONS } from "@/features/feed/utils/weather";
import { StageCombobox } from "@/features/stages/components/stage-combobox";
import { useStages } from "@/features/stages/hooks/use-stages.ts";
import { useActiveContext } from "@/store/use-active-context";

interface EditPostDialogProps {
	post: Post;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function EditPostDialog({
	post,
	open,
	onOpenChange,
}: EditPostDialogProps) {
	const [title, setTitle] = useState(post.title);
	const [description, setDescription] = useState(post.body);
	const [internal, setInternal] = useState(post.internal);
	const [weather, setWeather] = useState<PostWeather | undefined>(post.weather);
	const [stageId, setStageId] = useState(post.stage?.id ?? "");
	const [error, setError] = useState<string | null>(null);

	const queryClient = useQueryClient();
	const mutation = useUpdatePost(post.id);
	const activeObraId = useActiveContext((state) => state.activeObraId);
	const stagesQuery = useStages(activeObraId ?? undefined);
	const stages = stagesQuery.data?.stages ?? [];

	useEffect(() => {
		if (!open) return;
		setTitle(post.title);
		setDescription(post.body);
		setInternal(post.internal);
		setWeather(post.weather);
		setStageId(post.stage?.id ?? "");
		setError(null);
	}, [open, post]);

	const canSubmit =
		title.trim() !== "" && description.trim() !== "" && !mutation.isPending;

	function handleOpenChange(next: boolean) {
		if (mutation.isPending) return;
		onOpenChange(next);
	}

	async function submit() {
		setError(null);
		try {
			await mutation.mutateAsync({
				title,
				body: description,
				internal,
				weatherCondition: weather,
				stageId: stageId === (post.stage?.id ?? "") ? undefined : stageId,
			});
			toast.success("Relato atualizado!");
			onOpenChange(false);
		} catch (err) {
			if (err instanceof APIError && err.code === "not-found") {
				toast.error("Este relato não está mais disponível.");
				onOpenChange(false);
				queryClient.invalidateQueries({ queryKey: ["posts"] });
				queryClient.invalidateQueries({ queryKey: ["post", post.id] });
				return;
			}
			setError("Não foi possível salvar o relato. Tente novamente.");
		}
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="new-post__dialog">
				<DialogHeader className="new-post__header">
					<DialogTitle>Editar relato</DialogTitle>
					<DialogDescription>
						Ajuste as informações deste relato
					</DialogDescription>
				</DialogHeader>

				<div className="new-post__body">
					<div className="new-post__field">
						<span className="new-post__field-label">Título</span>
						<Input
							aria-label="Título"
							value={title}
							onChange={(event) => setTitle(event.target.value)}
						/>
					</div>

					<div className="new-post__field">
						<span className="new-post__field-label">Descrição</span>
						<Textarea
							aria-label="Descrição"
							className="new-post__textarea"
							value={description}
							onChange={(event) => setDescription(event.target.value)}
						/>
					</div>

					<div className="new-post__section">
						<span className="new-post__field-label">Clima</span>
						<div className="new-post__weather-row">
							<div className="new-post__weather-group">
								{WEATHER_OPTIONS.map((option) => {
									const Icon = option.icon;
									return (
										<button
											key={option.value}
											type="button"
											onClick={() => setWeather(option.value)}
											className={clsx(
												"new-post__weather-option",
												weather === option.value && "is-active",
											)}
										>
											<Icon
												className="feed-weather-icon"
												data-weather={option.value}
											/>{" "}
											{option.label}
										</button>
									);
								})}
							</div>
						</div>
					</div>

					<div className="new-post__field">
						<span className="new-post__field-label">Etapa do cronograma</span>
						<StageCombobox
							stages={stages}
							value={stageId}
							onChange={setStageId}
							placeholder="Nenhuma etapa"
							noneLabel="Nenhuma etapa"
							modal
							labelFromValue
						/>
					</div>

					<button
						type="button"
						onClick={() => setInternal((value) => !value)}
						className={clsx(
							"new-post__internal-toggle",
							internal && "is-internal",
						)}
					>
						<Checkbox
							checked={!internal}
							className="new-post__internal-checkbox"
						/>
						<div className="new-post__internal-body">
							<div className="new-post__internal-label">
								{internal ? (
									<>
										<Lock className="new-post__lock-icon" />
										Relato interno
									</>
								) : (
									<>
										<Eye className="new-post__visible-icon" />
										Visível para o cliente
									</>
								)}
							</div>
							<div className="new-post__internal-hint">
								{internal
									? "Este relato será visível apenas para a equipe técnica."
									: "O proprietário verá este relato no feed dele."}
							</div>
						</div>
					</button>

					{error && <p className="new-post__error">{error}</p>}
				</div>

				<DialogFooter className="new-post__footer">
					<DialogClose asChild>
						<Button variant="outline" disabled={mutation.isPending}>
							Cancelar
						</Button>
					</DialogClose>
					<Button onClick={submit} disabled={!canSubmit}>
						{mutation.isPending && <Loader2 className="new-post__spinner" />}
						Salvar alterações
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
